import { CompanionError } from '../_internal/error/CompanionError';
import { getCurrentSiteIdOrThrow } from '../sites/cache';
/**
 * Join url paths for consumption by the API modules
 *
 * @param path The path of the request (i.e. '/proxy/botr/video')
 * @param base The base URL (i.e. 'https://portal-api.jwplayer.com/v2')
 */
export function joinUrlGetHref(base, path) {
    let url;
    try {
        url = new URL(path, base);
    }
    catch (e) {
        throw new CompanionError({
            error: new Error(`TypeError: Invalid URL! Base: "${base}" and path: "${path}" can't be joined.`), // Throws TypeError
            code: 'URL_INVALID',
        });
    }
    return url.href;
}
joinUrlGetHref.ERR = {
    URL_INVALID: 'URL_INVALID',
};
/**
 * Parse request options
 *
 * @param requestOptions The `siteId` field inside `requestOptions` is an optional field. When not specified we will try to get current sideId or we will throw an error. When set to `null` it indicates that users specifically does not have or want to use a `siteId`.
 */
export function parseRequestOptions(requestOptions) {
    const parsedOptions = Object.assign({}, requestOptions);
    if (typeof (requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.siteId) === 'undefined') {
        parsedOptions.siteId = getCurrentSiteIdOrThrow();
    }
    else if ((requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.siteId) === null) {
        parsedOptions.siteId = undefined;
    }
    return parsedOptions;
}
export function constructJW09Query(q, i = 0) {
    var _a, _b;
    const strings = (_a = q.children) === null || _a === void 0 ? void 0 : _a.map((child) => {
        if (typeof child === 'object') {
            return constructJW09Query(child, i + 1);
        }
        else {
            return child === null || child === void 0 ? void 0 : child.trim();
        }
    }).filter(Boolean);
    if (!(strings === null || strings === void 0 ? void 0 : strings.length)) {
        return '';
    }
    let ret = '';
    if (q.negate) {
        ret += 'NOT ';
    }
    if (q.key) {
        ret += `${q.key}: `;
    }
    // Operator can only be omitted if there is a single child value, so the
    // default value is only to appease typescript, or in case typescript fails us
    const inner = strings.join(` ${(_b = q.operator) !== null && _b !== void 0 ? _b : 'AND'} `).trim();
    // Only wrap keys if inner clause has a colon or multiple inner clauses
    const wrapKey = q.key && (inner.includes(':') || strings.length > 1);
    const wrap = wrapKey || q.negate;
    ret += wrap ? `( ${inner} )` : inner;
    // Don't wrap first iteration
    return i === 0 ? ret : `( ${ret} )`;
}
export function convertJw09ParamsToV1Params(params) {
    const { page, page_length, sort } = params;
    const ret = {};
    if (typeof page_length !== 'undefined') {
        ret.result_limit = page_length;
    }
    ret.result_offset = 0;
    if (typeof page !== 'undefined' && typeof page_length !== 'undefined') {
        ret.result_offset = (page || 1) * page_length - page_length;
    }
    if (typeof sort !== 'undefined') {
        ret.order_by = sort === null || sort === void 0 ? void 0 : sort.map((s) => {
            return s.field + (s.order ? `:${s.order === 'asc' ? 'asc' : 'desc'}` : '');
        }).join(',');
    }
    return ret;
}
export function paramsToString(params) {
    if (!params) {
        return '';
    }
    const p = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (typeof value === 'undefined') {
            return;
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            return;
        }
        p.set(key, String(value));
    });
    if (params.sort && Array.isArray(params.sort)) {
        const str = params.sort
            .map((s) => {
            if (s && typeof s === 'object' && 'field' in s && 'order' in s && s.field && s.order) {
                return `${s.field}:${s.order}`;
            }
        })
            .filter(Boolean)
            .join(',');
        if (str) {
            p.set('sort', str);
        }
        else {
            p.delete('sort');
        }
    }
    if (params.q) {
        let str;
        if (typeof params.q === 'string') {
            str = params.q.trim();
        }
        else if (params.q) {
            str = constructJW09Query(params.q);
        }
        if (str) {
            p.set('q', str);
        }
        else {
            p.delete('q');
        }
    }
    return `?${p.toString()}`;
}
/**
 * Construct an upload url from a link block
 */
export function constructUploadUrl(link) {
    return `${link.protocol}s://${link.address}${link.path}${paramsToString({
        api_format: 'json',
        key: link.query.key,
        token: link.query.token,
    })}`;
}
